import { Injectable, inject } from '@angular/core';
import {combineLatest, from, lastValueFrom, Observable, of} from 'rxjs';
import {TenantService} from './tenant.service';
import {CourseSection} from '../models/course-section.model';
import {createFirestoreId, findLastBySeqNo, findUniqueMatchWithId, readCollectionWithIds, readDocumentWithId} from '../common/firestore-utils';
import {concatMap, first, map} from 'rxjs/operators';
import {Lesson} from '../models/lesson.model';
import {Course} from '../models/course.model';
import {Update} from '@ngrx/entity';
import {increment} from '@firebase/firestore';
import { collection, deleteDoc, doc, Firestore, limit, orderBy, query, setDoc, updateDoc, where, writeBatch } from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';

@Injectable()
export class LessonsDBService {
  private tenant = inject(TenantService);
  private firestore = inject(Firestore);
  private fs = inject(FirestoreService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  findLessonById(courseId:string, lessonId:string): Observable<Lesson> {

    const lessonPath = this.lessonsPath(courseId) + `/${lessonId}`;

    return readDocumentWithId<Lesson>(this.fs.docData(doc(this.firestore, lessonPath), {idField: "id"})).pipe(first());
  }


  loadCourseSection(courseId:string, sectionId:string) {
    return readDocumentWithId(this.fs.docData(doc(this.firestore,this.sectionsPath(courseId) + '/' + sectionId ), {idField: "id"})).pipe(first());

  }

  loadCourse(courseId: string) {

    return readDocumentWithId(this.fs.docData(doc(this.firestore, this.tenant.path(`courses/${courseId}`) ), {idField: "id"})).pipe(first());

  }


  loadCourseSections(courseId: string): Observable<CourseSection[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    const courseSectionsQuery = query(collection(this.firestore, coursePath + '/sections'), ...constraints);

    // Use getDocs for one-time snapshot read instead of persistent listener
    // This prevents interference with loadAllVisibleLiveSessionsAndWebinars()
    const courseSectionsData = from(this.fs.getDocs(courseSectionsQuery)).pipe(
      map((snapshot) => {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }),
      first() // Ensure it completes after one emission
    );

    return readCollectionWithIds<CourseSection[]>(
      courseSectionsData
      )
      .pipe(
      map(sections => sections.map(section => {return {...section, courseId}}))
      );

  }

  loadCourseLessons(courseId: string): Observable<Lesson[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    // Use getDocs for one-time snapshot read instead of persistent listener
    // This prevents interference with loadAllVisibleLiveSessionsAndWebinars()
    const lessonsQuery = query(collection(this.firestore, coursePath + '/lessons'), ...constraints);

    const courseLessonsData = from(this.fs.getDocs(lessonsQuery)).pipe(
      map((snapshot) => {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }),
      first() // Ensure it completes after one emission
    );

    return readCollectionWithIds<Lesson[]>(courseLessonsData);

    // return readCollectionWithIds<Lesson[]>(this.afs.collection(coursePath + '/lessons', ref => ref.orderBy('seqNo')));

  }

  loadCoursesLessonsCount(courseId: string): Observable<string> {
    const coursePath = this.tenant.path(`courses/${courseId}`);
    const q = query(
      collection(this.firestore, coursePath + '/lessons'),
      where('status', '==', 'published')
    );
    return from(this.fs.getDocs(q)).pipe(
      map(snapshot => snapshot.size.toString())
    );
  }

  loadAllVisibleLiveSessionsAndWebinars(): Observable<any[]> {
    const coursesPath = this.tenant.path('courses');
    const coursesQuery = query(
      collection(this.firestore, coursesPath),
      where('status', '==', 'published')
    );

    return this.fs.collectionData(coursesQuery, { idField: 'id' }).pipe(
      concatMap((courses: any[]) => {
        // Only include courses where reminders are not explicitly hidden
        const visibleCourses = (courses || []).filter(
          (course) => course.hideLiveSessionsAndWebinarsReminders !== true
        );

        if (!visibleCourses.length) {
          return from([[]]);
        }

        const lessonObservables = visibleCourses.map((course) => {
          const lessonsPath = this.tenant.path(`courses/${course.id}/lessons`);
          const sectionsPath = this.tenant.path(`courses/${course.id}/sections`);

          const lessons$ = this.fs.collectionData(
            query(
              collection(this.firestore, lessonsPath),
              where('type', 'in', ['live-session', 'webinar']),
              where('status', '==', 'published')
            ),
            { idField: 'id' }
          );

          const sections$ = this.fs.collectionData(
            query(collection(this.firestore, sectionsPath)),
            { idField: 'id' }
          );

          return combineLatest([lessons$, sections$]).pipe(
            map(([lessons, sections]: [any[], any[]]) => {
              return lessons.map((lesson) => {
                const section = sections.find(s => s.id === lesson.sectionId);
                return {
                  ...lesson,
                  courseId: course.id,
                  courseTitle: course.title,
                  courseUrl: course.url,
                  sectionSeqNo: section?.seqNo || 1
                };
              });
            })
          );
        });

        return combineLatest(
          lessonObservables.length > 0 ? lessonObservables : [from([[]])]
        );
      }),
      map((lessonsArrays) => {
        const flattened: any[] = [];
        lessonsArrays.forEach(arr => flattened.push(...arr));
        return flattened;
      })
    );
  }

  loadAllLiveSessions(): Observable<any[]> {
    const coursesPath = this.tenant.path('courses');
    const coursesQuery = query(
      collection(this.firestore, coursesPath),
      where('status', '==', 'published')
    );

    return this.fs.collectionData(coursesQuery, { idField: 'id' }).pipe(
      concatMap((courses: any[]) => {
        if (!courses.length) {
          return from([[]]);
        }

        const lessonObservables = courses.map((course) => {
          const lessonsPath = this.tenant.path(`courses/${course.id}/lessons`);
          const sectionsPath = this.tenant.path(`courses/${course.id}/sections`);

          const lessons$ = this.fs.collectionData(
            query(
              collection(this.firestore, lessonsPath),
              where('type', 'in', ['live-session', 'webinar']),
              where('status', '==', 'published')
            ),
            { idField: 'id' }
          );

          const sections$ = this.fs.collectionData(
            query(collection(this.firestore, sectionsPath)),
            { idField: 'id' }
          );

          return combineLatest([lessons$, sections$]).pipe(
            map(([lessons, sections]: [any[], any[]]) => {
              return lessons.map((lesson) => {
                const section = sections.find(s => s.id === lesson.sectionId);
                return {
                  ...lesson,
                  courseId: course.id,
                  courseTitle: course.title,
                  courseUrl: course.url,
                  sectionSeqNo: section?.seqNo || 1
                };
              });
            })
          );
        });

        return combineLatest(lessonObservables.length > 0 ? lessonObservables : [from([[]])]);
      }),
      map((lessonsArrays) => {
        const flattened: any[] = [];
        lessonsArrays.forEach(arr => flattened.push(...arr));
        return flattened;
      })
    );
  }

  async loadCoursesLessonsPublishedCount(coursesIds: string[]){

    let count = 0;

    for (const courseId of coursesIds) {

      const coursePath = this.tenant.path(`courses/${courseId}`);
      const constraints = [
        where("status", "==", "published"),
      ]

      const courseLessonsData = await lastValueFrom(
        this.fs.collectionData(query(collection(this.firestore, coursePath + '/lessons'), ...constraints), {idField: "id"}).pipe(first())
    )

      count += courseLessonsData.length

    }

    return count

  }


  loadCourseSectionsPage(courseId: string, afterSeqNo: number, pageSize: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      orderBy('seqNo'),
      where('seqNo', '>', afterSeqNo),
      limit(pageSize)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadCourseSectionsBefore(courseId: string, beforeSeqNo: number, pageSize: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      orderBy('seqNo', 'desc'),
      where('seqNo', '<', beforeSeqNo),
      limit(pageSize)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadSectionBySeqNo(courseId: string, seqNo: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      where('seqNo', '==', seqNo),
      limit(1)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadLessonsForSections(courseId: string, sectionIds: string[]): Observable<Lesson[]> {
    if (sectionIds.length === 0) return of([]);

    // Firestore `in` query supports at most 30 values. Chunk the IDs and
    // combine the results so courses with many sections don't throw.
    const CHUNK = 30;
    const chunks: string[][] = [];
    for (let i = 0; i < sectionIds.length; i += CHUNK) {
      chunks.push(sectionIds.slice(i, i + CHUNK));
    }

    const queries$ = chunks.map(chunk =>
      from(this.fs.getDocs(query(
        collection(this.firestore, this.lessonsPath(courseId)),
        where('sectionId', 'in', chunk),
        orderBy('seqNo')
      ))).pipe(
        map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson)))
      )
    );

    return combineLatest(queries$).pipe(map(arrays => arrays.flat()));
  }

  addNewSection(course: Course, title: string):Observable<CourseSection> {
    return findLastBySeqNo<CourseSection>(this.firestore, this.sectionsPath(course.id))
      .pipe(
        concatMap(last => {

          const newSection: any = {
            title,
            seqNo: last ? (last.seqNo + 1) : 1
          };

            const docRef = doc(this.firestore, this.sectionsPath(course.id) + '/' + createFirestoreId(this.firestore))
            const addSectionAsync = setDoc(
              docRef,
              newSection
            ).then(() => {
              return {
                id: docRef.id,
                courseId: course.id,
                ...newSection
              }
            })

          return from(addSectionAsync);
        })
      );
  }

  saveSection(courseId: string, sectionId:string, update: Update<CourseSection>): Observable<any> {

    const sectionDocRef = doc(this.firestore, this.sectionsPath(courseId), update.id as string)

    const updateRef = updateDoc(sectionDocRef, update.changes)

    return from(updateRef);

  }


  addNewLesson(courseId: string, sectionId: string, title: string, type: string, newLessonSeqNo:number, changes?: Partial<Lesson>, lessonId?: string): Observable<Lesson> {

    const calendarBookingDefaults = type === 'calendar-booking' ? {
      calendarBookingButtonText: "Book Now",
      calendarBookingButtonTextColor: "#ffffff",
      calendarBookingButtonColor: "#EB3B3B",
      calendarBookingButtonIconColor: "#ffffff"
    } : {};

    const newLesson: any = {
      title,
      type,
      seqNo: newLessonSeqNo,
      sectionId,
      status: 'draft',
      free:false,
      ...calendarBookingDefaults,
      ...changes

    };

    let lessonFirestoreId: string;

    if (lessonId) {
      lessonFirestoreId = lessonId;
    } else {
      lessonFirestoreId = createFirestoreId(this.firestore);
    }

    const docRef = doc(this.firestore, this.lessonsPath(courseId), lessonFirestoreId)
    const addLessonAsync = setDoc(docRef, newLesson).then(() => {
      return {
        id: docRef.id,
        ...newLesson
      }
    })

    return from(addLessonAsync);
  }

  saveLesson(courseId: string, update: Update<Lesson>): Observable<any> {

    const saveLessonAsync = updateDoc(doc(this.firestore, this.lessonsPath(courseId), update.id as string), update.changes)

    return from(saveLessonAsync);

  }

  deleteSection(courseId: string, sectionId: string): Observable<any> {

    return from(deleteDoc(doc(this.firestore, this.sectionsPath(courseId), sectionId)))

  }

  deleteLesson(courseId: string, lessonId:string, videoDuration:number): Observable<any> {

    const batch = writeBatch(this.firestore);

    const lessonRef = doc(this.firestore,this.lessonsPath(courseId), lessonId)

    batch.delete(lessonRef);

    if (videoDuration) {
      const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}`);

      batch.update(courseRef, {
        totalDuration: increment(-1 * videoDuration)
      });
    }

    return from(batch.commit());
  }

  private findLastLessonInSection(courseId:string, sectionId:string): Observable<Lesson> {

    const constraints = [
      where('sectionId', '==', sectionId),
      orderBy('seqNo', 'desc'),
      limit(1)
    ]

    const query$ = this.fs.collectionData(
      query(
        collection(this.firestore, this.lessonsPath(courseId)), ...constraints),
      {idField: "id"}
      )

    // const query$ = this.afs.collection<Lesson>(this.lessonsPath(courseId),
    //     ref => ref.where('sectionId', '==', sectionId).orderBy('seqNo', 'desc').send-limit-email(1));

    return findUniqueMatchWithId(query$).pipe(first()) as Observable<Lesson>;
  }


  suscribeToLesson(courseId: string, lessonId:string): Observable<Lesson> {
    const lessonsData = this.fs.docData(doc(this.firestore, this.lessonsPath(courseId) + '/' + lessonId), {idField: "id"})
    return readDocumentWithId(lessonsData);
  }


  private sectionsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/sections`);
  }

  private lessonsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/lessons`);
  }

  addLessonsSections(courseId:string, lessons: Lesson[], sections: CourseSection[]) {

    const batch = writeBatch(this.firestore);

    lessons.forEach(lesson => {
      const lessonRef = doc(this.firestore, this.lessonsPath(courseId), lesson.id);
      // batch.set(lessonRef, lesson);
      batch.set(lessonRef, lesson, { merge: true });

    });

    sections.forEach(section => {
      const sectionRef = doc(this.firestore, this.sectionsPath(courseId), section.id);
      batch.set(sectionRef, section);
    });

    return from(batch.commit());

  }

  updateLessons(courseId:string, changes: Update<Lesson>[]) {

    const batch = writeBatch(this.firestore);

    changes.forEach(change => {
      const lessonRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}/lessons/${change.id}`);
      batch.update(lessonRef, change.changes);
    });

    return from(batch.commit());

  }
}




